import type { Locale } from '../api/schemas';
import { createObject } from './factory';
import { keyBetween } from './fractionalIndex';
import { layoutMindmap } from './mindmapLayout';
import { STICKY_COLORS } from './palette';
import type { BoardMeta, BoardObject, ObjectOf, ObjectType } from './types';

export const TEMPLATE_IDS = [
  'blank',
  'retro',
  'kanban',
  'mindmap',
  'storymap',
  'swot',
  'lean',
  'roadmap',
  'flowchart',
  'brainstorm',
  'weekly',
] as const;
export type TemplateId = (typeof TEMPLATE_IDS)[number];

export interface TemplateContent {
  objects: BoardObject[];
  meta: Partial<BoardMeta>;
}

type L = Record<Locale, string>;
const tr = (l: L, locale: Locale) => l[locale];

/** Small helper that assigns ascending z keys and frame parents. */
class Builder {
  readonly objects: BoardObject[] = [];
  private z: string | null = null;

  constructor(private readonly createdBy: string) {}

  add<T extends ObjectType>(
    type: T,
    x: number,
    y: number,
    o: Partial<Omit<ObjectOf<T>, 'type'>> = {},
  ): ObjectOf<T> {
    this.z = keyBetween(this.z, null);
    const obj = createObject(type, { x, y, z: this.z, createdBy: this.createdBy }, o);
    this.objects.push(obj);
    return obj;
  }

  frame(x: number, y: number, w: number, h: number, title: string, fill = '#ffffff') {
    return this.add('frame', x, y, { w, h, title, fill });
  }

  sticky(
    parent: { id: string } | null,
    x: number,
    y: number,
    text: string,
    color: string = STICKY_COLORS[0],
    size = 160,
  ) {
    return this.add('sticky', x, y, {
      text,
      color,
      w: size,
      h: size,
      parentId: parent?.id ?? null,
    });
  }

  text(
    parent: { id: string } | null,
    x: number,
    y: number,
    text: string,
    fontSize = 24,
    w = 400,
    bold = true,
  ) {
    return this.add('text', x, y, {
      text,
      w,
      h: fontSize * 1.6,
      parentId: parent?.id ?? null,
      style: {
        fontSize,
        fontFamily: 'sans',
        color: '#1a1a2e',
        bold,
        italic: false,
        underline: false,
        strike: false,
        align: 'left',
        valign: 'top',
      },
    });
  }

  connect(a: { id: string }, b: { id: string }, label = '') {
    return this.add('connector', 0, 0, {
      from: { objectId: a.id, anchor: 'auto', x: 0, y: 0 },
      to: { objectId: b.id, anchor: 'auto', x: 0, y: 0 },
      label,
    });
  }
}

function columns(b: Builder, titles: string[], colors: string[], hint: string, frameH = 900) {
  const w = 440;
  const frames = titles.map((title, i) => b.frame(i * (w + 40), 0, w, frameH, title, '#f8fafc'));
  frames.forEach((f, i) => b.sticky(f, f.x + 40, 80, hint, colors[i % colors.length]));
  return frames;
}

const BUILDERS: Record<TemplateId, (b: Builder, locale: Locale) => Partial<BoardMeta>> = {
  blank: () => ({}),

  retro: (b, l) => {
    const titles =
      l === 'ru'
        ? ['Начать делать', 'Перестать делать', 'Продолжать делать']
        : ['Start', 'Stop', 'Continue'];
    columns(
      b,
      titles,
      [STICKY_COLORS[7], STICKY_COLORS[4], STICKY_COLORS[8]],
      tr({ ru: 'Ваша идея…', en: 'Your idea…' }, l),
    );
    return {};
  },

  kanban: (b, l) => {
    const titles =
      l === 'ru'
        ? ['Бэклог', 'К работе', 'В работе', 'Готово']
        : ['Backlog', 'To do', 'In progress', 'Done'];
    const frames = titles.map((t, i) => b.frame(i * 380, 0, 340, 900, t, '#f1f5f9'));
    frames.slice(0, 3).forEach((f, i) =>
      b.add('card', f.x + 30, 80, {
        parentId: f.id,
        title: tr({ ru: `Задача ${i + 1}`, en: `Task ${i + 1}` }, l),
        description: tr({ ru: 'Описание задачи', en: 'Task description' }, l),
        color: ['#6366f1', '#f97316', '#22c55e'][i]!,
      }),
    );
    return {};
  },

  mindmap: (b, l) => {
    const root = b.add('mindmap', 0, 0, {
      text: tr({ ru: 'Центральная идея', en: 'Central idea' }, l),
      w: 220,
      h: 56,
      color: '#4f46e5',
    });
    const ideas =
      l === 'ru'
        ? ['Идея 1', 'Идея 2', 'Идея 3', 'Идея 4']
        : ['Idea 1', 'Idea 2', 'Idea 3', 'Idea 4'];
    let order: string | null = null;
    ideas.forEach((text, i) => {
      order = keyBetween(order, null);
      b.add('mindmap', 0, 0, {
        text,
        parentNode: root.id,
        rootId: root.id,
        side: i % 2 === 0 ? 'right' : 'left',
        order,
        color: ['#22c55e', '#f97316', '#0ea5e9', '#ec4899'][i]!,
      });
    });
    return {};
  },

  storymap: (b, l) => {
    const acts =
      l === 'ru'
        ? ['Найти', 'Выбрать', 'Купить', 'Получить']
        : ['Discover', 'Choose', 'Buy', 'Receive'];
    b.text(null, 0, -80, tr({ ru: 'User Story Map', en: 'User Story Map' }, l), 36);
    acts.forEach((a, i) => {
      b.sticky(null, i * 200, 0, a, STICKY_COLORS[9], 170);
      b.sticky(null, i * 200, 220, tr({ ru: 'История', en: 'Story' }, l), STICKY_COLORS[0], 170);
    });
    b.text(null, -260, 280, tr({ ru: 'Релиз 1', en: 'Release 1' }, l), 20, 220);
    return {};
  },

  swot: (b, l) => {
    const t =
      l === 'ru'
        ? ['Сильные стороны', 'Слабые стороны', 'Возможности', 'Угрозы']
        : ['Strengths', 'Weaknesses', 'Opportunities', 'Threats'];
    const fills = ['#ecfdf5', '#fef2f2', '#eff6ff', '#fffbeb'];
    t.forEach((title, i) =>
      b.frame((i % 2) * 620, Math.floor(i / 2) * 520, 600, 500, title, fills[i]),
    );
    return {};
  },

  lean: (b, l) => {
    const cells: [string, string, number, number, number, number][] =
      l === 'ru'
        ? [
            ['Проблема', '', 0, 0, 1, 2],
            ['Решение', '', 1, 0, 1, 1],
            ['Ключевые метрики', '', 1, 1, 1, 1],
            ['Ценностное предложение', '', 2, 0, 1, 2],
            ['Нечестное преимущество', '', 3, 0, 1, 1],
            ['Каналы', '', 3, 1, 1, 1],
            ['Сегменты клиентов', '', 4, 0, 1, 2],
            ['Структура затрат', '', 0, 2, 2.5, 1],
            ['Потоки доходов', '', 2.5, 2, 2.5, 1],
          ]
        : [
            ['Problem', '', 0, 0, 1, 2],
            ['Solution', '', 1, 0, 1, 1],
            ['Key metrics', '', 1, 1, 1, 1],
            ['Unique value proposition', '', 2, 0, 1, 2],
            ['Unfair advantage', '', 3, 0, 1, 1],
            ['Channels', '', 3, 1, 1, 1],
            ['Customer segments', '', 4, 0, 1, 2],
            ['Cost structure', '', 0, 2, 2.5, 1],
            ['Revenue streams', '', 2.5, 2, 2.5, 1],
          ];
    const cw = 320;
    const ch = 340;
    for (const [title, , cx, cy, w, h] of cells)
      b.frame(cx * cw, cy * ch, w * cw - 10, h * ch - 10, title, '#f8fafc');
    return {};
  },

  roadmap: (b, l) => {
    const q = ['Q1', 'Q2', 'Q3', 'Q4'];
    const lanes =
      l === 'ru' ? ['Продукт', 'Дизайн', 'Разработка'] : ['Product', 'Design', 'Engineering'];
    b.text(null, 0, -100, tr({ ru: 'Роадмап', en: 'Roadmap' }, l), 36);
    q.forEach((t, i) =>
      b.add('shape', 200 + i * 360, -30, {
        w: 340,
        h: 50,
        text: t,
        fill: '#e0e7ff',
        stroke: '#6366f1',
        shape: 'roundRect',
      }),
    );
    lanes.forEach((t, i) => {
      b.add('shape', 0, 40 + i * 220, {
        w: 180,
        h: 200,
        text: t,
        fill: '#f1f5f9',
        stroke: '#cbd5e1',
        shape: 'roundRect',
      });
      b.add('card', 220 + i * 360, 70 + i * 220, {
        title: tr({ ru: 'Инициатива', en: 'Initiative' }, l),
        color: ['#6366f1', '#ec4899', '#14b8a6'][i]!,
      });
    });
    return {};
  },

  flowchart: (b, l) => {
    const start = b.add('shape', 0, 0, {
      shape: 'ellipse',
      w: 180,
      h: 80,
      text: tr({ ru: 'Старт', en: 'Start' }, l),
      fill: '#dcfce7',
      stroke: '#16a34a',
    });
    const step = b.add('shape', 0, 160, {
      shape: 'roundRect',
      w: 180,
      h: 90,
      text: tr({ ru: 'Действие', en: 'Action' }, l),
      fill: '#dbeafe',
      stroke: '#2563eb',
    });
    const cond = b.add('shape', -10, 330, {
      shape: 'diamond',
      w: 200,
      h: 140,
      text: tr({ ru: 'Условие?', en: 'Condition?' }, l),
      fill: '#fef9c3',
      stroke: '#ca8a04',
    });
    const yes = b.add('shape', 0, 560, {
      shape: 'ellipse',
      w: 180,
      h: 80,
      text: tr({ ru: 'Конец', en: 'End' }, l),
      fill: '#fee2e2',
      stroke: '#dc2626',
    });
    const no = b.add('shape', 300, 355, {
      shape: 'roundRect',
      w: 180,
      h: 90,
      text: tr({ ru: 'Исправить', en: 'Fix' }, l),
      fill: '#dbeafe',
      stroke: '#2563eb',
    });
    b.connect(start, step);
    b.connect(step, cond);
    b.connect(cond, yes, tr({ ru: 'да', en: 'yes' }, l));
    b.connect(cond, no, tr({ ru: 'нет', en: 'no' }, l));
    b.connect(no, step);
    return {};
  },

  brainstorm: (b, l) => {
    const center = b.add('shape', 0, 0, {
      shape: 'ellipse',
      w: 300,
      h: 160,
      text: tr({ ru: 'Вопрос для штурма', en: 'Brainstorm question' }, l),
      fill: '#e0e7ff',
      stroke: '#4f46e5',
      style: {
        fontSize: 22,
        fontFamily: 'sans',
        color: '#1a1a2e',
        bold: true,
        italic: false,
        underline: false,
        strike: false,
        align: 'center',
        valign: 'middle',
      },
    });
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      b.sticky(
        null,
        center.x + 150 + Math.cos(a) * 380 - 80,
        center.y + 80 + Math.sin(a) * 300 - 80,
        '',
        STICKY_COLORS[(i * 3) % 12],
        160,
      );
    }
    return {};
  },

  weekly: (b, l) => {
    const days =
      l === 'ru'
        ? ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
        : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    b.text(null, 0, -90, tr({ ru: 'Планирование недели', en: 'Weekly planning' }, l), 36, 600);
    days.forEach((d, i) => b.frame(i * 280, 0, 260, 640, d, i >= 5 ? '#fdf2f8' : '#f8fafc'));
    return {};
  },
};

export function buildTemplate(id: string, locale: Locale, createdBy: string): TemplateContent {
  const builder = new Builder(createdBy);
  const fn =
    BUILDERS[(TEMPLATE_IDS as readonly string[]).includes(id) ? (id as TemplateId) : 'blank'];
  const meta = fn(builder, locale);
  const nodes = builder.objects.filter((o): o is ObjectOf<'mindmap'> => o.type === 'mindmap');
  for (const [id, pos] of layoutMindmap(nodes))
    Object.assign(
      nodes.find((n) => n.id === id)!,
      pos,
    );
  const frameOrder = builder.objects.filter((o) => o.type === 'frame').map((o) => o.id);
  return { objects: builder.objects, meta: { frameOrder, ...meta } };
}

export function isTemplateId(id: string): id is TemplateId {
  return (TEMPLATE_IDS as readonly string[]).includes(id);
}
